import {
	FormCondition,
	ConditionalRule,
	FormValue,
	FormOperator,
} from '@models/client-intake';

export function evaluateConditionalRule(
	rule: ConditionalRule | FormCondition,
	getFormValue: (questionId: string) => FormValue,
): boolean {
	if (!rule) return true;

	if ('questionId' in rule) {
		return evaluateCondition(rule as FormCondition, getFormValue);
	}

	const conditionalRule = rule as ConditionalRule;

	if ('and' in conditionalRule) {
		return conditionalRule.and.every((item) =>
			evaluateItem(item, getFormValue),
		);
	}

	if ('or' in conditionalRule) {
		return conditionalRule.or.some((item) => evaluateItem(item, getFormValue));
	}

	return false;
}

function evaluateItem(
	item: FormCondition | ConditionalRule,
	getFormValue: (questionId: string) => FormValue,
): boolean {
	if ('questionId' in item) {
		return evaluateCondition(item as FormCondition, getFormValue);
	}

	return evaluateConditionalRule(item as ConditionalRule, getFormValue);
}

function evaluateCondition(
	condition: FormCondition,
	getFormValue: (questionId: string) => FormValue,
): boolean {
	const fieldValue = getFormValue(condition.questionId);

	if (condition.operator === FormOperator.IS) {
		const normalizedConditionValue = condition.value.toLowerCase();
		if (normalizedConditionValue === 'null') {
			return (
				fieldValue === null || fieldValue === undefined || fieldValue === ''
			);
		}
		if (normalizedConditionValue === 'not null') {
			return (
				fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
			);
		}
		if (normalizedConditionValue === 'true') return fieldValue === true;
		if (normalizedConditionValue === 'false') return fieldValue === false;
		return false;
	}

	if (fieldValue === '' || fieldValue === null || fieldValue === undefined) {
		return false;
	}

	let parsedValue: FormValue = fieldValue;
	let parsedConditionValue: string = condition.value;

	if (
		condition.operator === FormOperator.EQUALS ||
		condition.operator === FormOperator.NOT_EQUALS
	) {
		parsedValue = String(fieldValue);
	} else if (
		[
			FormOperator.GREATER_THAN,
			FormOperator.LESS_THAN,
			FormOperator.GREATER_THAN_OR_EQUAL,
			FormOperator.LESS_THAN_OR_EQUAL,
		].includes(condition.operator)
	) {
		parsedValue = parseFloat(String(fieldValue));
		parsedConditionValue = condition.value;

		if (isNaN(parsedValue as number)) {
			return false;
		}
	}

	switch (condition.operator) {
		case FormOperator.EQUALS:
			return parsedValue === parsedConditionValue;
		case FormOperator.NOT_EQUALS:
			return parsedValue !== parsedConditionValue;
		case FormOperator.GREATER_THAN:
			return (parsedValue as number) > parseFloat(parsedConditionValue);
		case FormOperator.LESS_THAN:
			return (parsedValue as number) < parseFloat(parsedConditionValue);
		case FormOperator.GREATER_THAN_OR_EQUAL:
			return (parsedValue as number) >= parseFloat(parsedConditionValue);
		case FormOperator.LESS_THAN_OR_EQUAL:
			return (parsedValue as number) <= parseFloat(parsedConditionValue);
		case FormOperator.IN:
			try {
				const arrayValue = JSON.parse(parsedConditionValue);
				return Array.isArray(arrayValue) && arrayValue.includes(parsedValue);
			} catch {
				return false;
			}
		default:
			return true;
	}
}
